/**
 * @fileoverview Hero1 module animation initializer using GSAP and Swiper.
 * @author
 * Dreamers of Day
 */

import Swiper, { Autoplay, EffectCube } from 'swiper';
import { gsap } from 'gsap';
import SplitText from '../../../../../global/js/vendors/gsap-splittext/splittext';

// Register GSAP plugin for text splitting animation
gsap.registerPlugin(SplitText);

/**
 * Initializes animations and interactions for the hero1 module.
 * Adds scroll-triggered effects, text splits, Swiper slides, and staged GSAP timelines.
 *
 * @function hero1
 * @returns {void}
 */
function hero1() {
  // Register Swiper modules (must be done before Swiper is used)
  Swiper.use([Autoplay, EffectCube]);

  // Find the module with data attribute
  const module = $('[data-module="hero1"]');

  // Exit early if module is not present
  if (!module.length) return;

  // Query all relevant elements within the module for animation sequences
  const largeTitle = module.find('.o-hero-1__large-title');
  const largeTitleLetter = module.find('.a-svg-breckenridge-hero__large-title-letter');
  const title = module.find('.a-title--animated');
  const row1 = module.find('.o-hero-1__title-row--1 .o-hero-1__title-row-inner-text');
  const row2 = module.find('.o-hero-1__title-row--2 .o-hero-1__title-row-inner-text');
  const slides = module.find('.js-hero-1-title-slides');
  const row3 = module.find('.o-hero-1__title-row--3 .o-hero-1__title-row-inner-text');
  // const bgHeroSet = module.find('.m-background-set-hero');
  // const heroImage = module.find('.a-img');
  // const heroVideo = module.find('.a-video');
  const headerLogoWrapper = document.querySelector('.o-header-1__logo-wrapper');

  // Create a timeline for background and media animation
  const tl = gsap.timeline();

  // Animate the background element
  /*tl.from(bgHeroSet, {
    duration: 1.4,
    autoAlpha: 0,
    yPercent: 100,
    ease: 'expo.inOut',
  });*/

  // Animate the hero image if it exists
  /*if (heroImage.length) {
    tl.from(
      heroImage,
      {
        duration: 1.4,
        yPercent: -80,
        scale: 1.2,
        ease: 'expo.inOut',
      },
      '<'
    );
  }*/

  // Animate the hero video if it exists
  /*if (heroVideo.length) {
    tl.from(
      heroVideo,
      {
        duration: 1.4,
        yPercent: -80,
        scale: 1.2,
        ease: 'expo.inOut',
      },
      '<'
    );
  }*/

  // Animate individual SVG letters if present
  if (largeTitleLetter.length) {
    tl.fromTo(
      largeTitleLetter,
      { autoAlpha: 0, y: -50 },
      {
        autoAlpha: 1,
        y: 0,
        ease: 'power4.out',
        duration: 1,
        stagger: {
          each: 0.1,
          from: 'end',
        },
      },
      '>'
    );
  }

  // Declare title animation timeline outside function scope for reuse
  let titleTl;

  /**
   * Triggers the title animation timeline, animating logo, rows, and Swiper.
   *
   * @function titleAnimation
   * @returns {void}
   */
  function titleAnimation() {
    // New timeline for title elements
    titleTl = gsap.timeline();

    // Fade in header logo wrapper if available
    if (headerLogoWrapper) {
      titleTl.fromTo(
        headerLogoWrapper,
        { autoAlpha: 0, scale: 0 },
        { autoAlpha: 1, scale: 1, duration: 0.8, ease: 'power4.inOut' }
      );
    }

    // Fade in animated title
    if (title.length) {
      titleTl.fromTo(
        title,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.3, ease: 'power4.inOut' },
        '<' // sync with logo
      );

      // Animate row1 words if exists
      if (row1.length) {
        const splitRow1 = new SplitText(row1, {
          type: 'words',
          linesClass: 'overflow-hidden inline-block',
        });
        titleTl.from(splitRow1.words, {
          yPercent: 100,
          autoAlpha: 0,
          duration: 0.8,
          ease: 'power4.out',
          stagger: 0.05,
        });
      }

      // Animate row2 words
      if (row2.length) {
        const splitRow2 = new SplitText(row2, {
          type: 'words',
          linesClass: 'overflow-hidden inline-block',
        });
        titleTl.from(
          splitRow2.words,
          {
            yPercent: 100,
            autoAlpha: 0,
            duration: 0.8,
            ease: 'power4.out',
            stagger: 0.05,
          },
          '-=0.5'
        );
      }

      // Fade in Swiper container and initialize Swiper
      if (slides.length) {
        titleTl.fromTo(
          slides,
          { autoAlpha: 0 },
          { autoAlpha: 1, duration: 0.5, ease: 'power2.out' },
          '-=0.3'
        );

        // Initialize Swiper instance after fade-in
        new Swiper(slides[0], {
          loop: true, // Continuous loop mode
          direction: 'vertical', // Vertical scrolling effect
          effect: 'fade', // Apply cube effect
          speed: 1000, // Animation speed
          allowTouchMove: false, // Disable user interaction
          cubeEffect: { shadow: false, slideShadows: false }, // Remove shadow effects
          autoplay: { delay: 4000 }, // Auto-slide every 4 seconds
        });
      }

      // Animate row3 words
      if (row3.length) {
        const splitRow3 = new SplitText(row3, {
          type: 'words',
          linesClass: 'overflow-hidden inline-block',
        });
        titleTl.from(
          splitRow3.words,
          {
            yPercent: 100,
            autoAlpha: 0,
            duration: 0.8,
            ease: 'power4.out',
            stagger: 0.3,
          },
          '-=0.5'
        );
      }
    }
  }

  // Scroll-trigger animation for large title
  /*if (largeTitle.length) {
    const startValue = document.querySelector('.o-header-top-bar-1')
      ? 'top top'
      : 'top+=6px top';

    // Pin and animate large title on scroll
    tl.to(largeTitle, {
      scrollTrigger: {
        trigger: '.o-hero-1',
        start: startValue,
        end: 'bottom bottom',
        scrub: true,
        pin: true,
        onLeave: () => {
          titleAnimation();
        },
        onEnterBack: () => {
          if (titleTl) {
            titleTl.pause(0);
          }
        },
      },
      scale: 0.2,
      opacity: 0,
      ease: 'none',
      duration: 0.6,
    });
  } else {
    // If no large title, play title animation right after hero media
    tl.add(() => {
      titleAnimation();
    }, '>');
  }*/

  if (largeTitle.length) {
    tl.to(largeTitle, {
      scale: 0.2,
      opacity: 0,
      ease: 'none',
      duration: 0.1,
    });

    tl.add(() => {
      titleAnimation();
    }, '>');
  } else {
    tl.add(() => {
      titleAnimation();
    }, '>');
  }
}

export default hero1;
